import React, { forwardRef, useCallback, useEffect, useId, useRef } from "react";

import ButtonUI from "./ButtonUI";
import { pressPoint } from "./glass";
import renderIcon from "./renderIcon";

const cx = (...a) => a.filter(Boolean).join(" ");

/**
 * SearchBarUI — singura căutare a aplicației. Înlocuiește câmpul-pastilă „Caută…”
 * din popup-uri (createReservationPopup__search-input și încă 14 copii) cu rândul
 * lui de butoane (…__search-header), câmpul compact din uneltele de examen
 * (exam-admin__input), căutarea lipită de lupă din calendare și din Personal
 * (ultra-toolbar-search, personalPageUI__search) și SearchToggle (se deschide
 * din lupă). Stilurile: scss/UI/_searchBarUI.scss.
 *
 * variant: pill (implicit: pastila black-p, rază 25, 13×20, 14px) |
 *          compact (rază 12, 10×12, 13px: bara uneltelor de examen) |
 *          joined (36px, rază 32 8 8 32, 13px, lupa lipită — rază 8 32 32 8)
 * surface: page = pe fundalul paginii, câmpul black-s
 * onClear: × în dreapta câmpului cât timp e text și câmpul nu e dezactivat; Escape golește
 * actions: butoanele de după câmp (Înapoi, Creează, Adaugă) · leading: cele dinainte;
 *          cu oricare, căutarea devine rândul de căutare (6px între, 6px sus și jos)
 * onSubmit: lupa lipită (joined) și Enter rulează căutarea (onSubmit(value));
 *           fără onSubmit, lupa doar pune cursorul în câmp (Personal)
 * submitLabel: eticheta lupei („Caută”)
 * nav: { index, total, onPrev, onNext, hidden, title, label } — contorul „3/12” cu ◀ ▶
 *      (calendarele); hidden: „+N” rezultate care nu se văd, cu explicația în title;
 *      label înlocuiește textul „3/12”. Cu rezultate, ← și → din câmp merg la rezultatul
 *      anterior / următor (ca în calendare).
 * collapsible: căutarea care se deschide din lupă (SearchToggle) — open, onToggle,
 *      openLabel („Caută”) / closeLabel („Închide căutarea”), autoFocusOnOpen (implicit da);
 *      închisă, câmpul nu se vede și e scos din ordinea tastaturii
 * placeholder: implicit „Caută…”; eticheta pentru cititoarele de ecran e `aria-label`
 *      sau, implicit, placeholder-ul
 * className / style: pe rândul întreg. Restul (value, onChange, disabled, autoFocus,
 *      onKeyDown, name…) merge pe <input type="search">; ref-ul e al câmpului.
 */
const SearchBarUI = forwardRef(function SearchBarUI(
   {
      variant = "pill",
      surface,
      actions,
      leading,
      onClear,
      onSubmit,
      submitLabel = "Caută",
      nav,
      collapsible = false,
      open,
      onToggle,
      openLabel = "Caută",
      closeLabel = "Închide căutarea",
      autoFocusOnOpen = true,
      placeholder = "Caută…",
      value,
      disabled = false,
      id,
      tabIndex,
      onKeyDown,
      className = "",
      style,
      "aria-label": ariaLabel,
      ...rest
   },
   ref,
) {
   const autoId = useId();
   const inputId = id || `searchBarUI-${autoId}`;
   const inputRef = useRef(null);
   const isOpen = !collapsible || Boolean(open);
   const hidden = collapsible && !isOpen;
   const clearable = typeof onClear === "function" && !disabled && value != null && String(value) !== "";
   const navTotal = nav ? Number(nav.total) || 0 : 0;

   // ref-ul primit + al nostru (focusul la deschidere și de pe lupă)
   const setRef = useCallback(
      (node) => {
         inputRef.current = node;
         if (typeof ref === "function") ref(node);
         else if (ref) ref.current = node;
      },
      [ref],
   );

   // SearchToggle: focus în câmp după ce se deschide (80ms, ca să nu sară animația);
   // doar la deschidere, nu și când pagina se încarcă deja deschisă
   const wasOpen = useRef(isOpen);
   useEffect(() => {
      const opened = isOpen && !wasOpen.current;
      wasOpen.current = isOpen;
      if (!collapsible || !opened || !autoFocusOnOpen) return undefined;
      const t = setTimeout(() => inputRef.current?.focus?.(), 80);
      return () => clearTimeout(t);
   }, [isOpen, collapsible, autoFocusOnOpen]);

   const submit = () => {
      if (onSubmit) onSubmit(value);
      else inputRef.current?.focus?.();
   };

   // tastele din calendare: Enter caută, Escape golește, ← → trec prin rezultate
   const handleKeyDown = (e) => {
      onKeyDown?.(e);
      if (e.defaultPrevented) return;
      if (e.key === "Enter" && onSubmit) {
         e.preventDefault();
         onSubmit(value);
      } else if (e.key === "Escape" && clearable) {
         e.preventDefault();
         onClear();
      } else if (navTotal && (e.key === "ArrowLeft" || e.key === "ArrowRight")) {
         e.preventDefault();
         (e.key === "ArrowLeft" ? nav.onPrev : nav.onNext)?.();
      }
   };

   const navLabel = nav ? (nav.label ?? (navTotal ? `${(Number(nav.index) || 0) + 1}/${navTotal}` : "0/0")) : null;

   return (
      <div
         className={cx(
            "searchBarUI",
            variant !== "pill" && `searchBarUI--${variant}`,
            surface === "page" && "searchBarUI--page",
            (actions || leading) && "searchBarUI--row",
            collapsible && "searchBarUI--collapsible",
            collapsible && isOpen && "is-open",
            clearable && "is-clearable",
            disabled && "is-disabled",
            className,
         )}
         style={style}
      >
         {leading && <div className="searchBarUI__actions">{leading}</div>}

         <div className="searchBarUI__field" aria-hidden={hidden || undefined}>
            <input
               {...rest}
               ref={setRef}
               id={inputId}
               type="search"
               className="searchBarUI__input"
               placeholder={placeholder}
               aria-label={ariaLabel || placeholder}
               value={value}
               disabled={disabled}
               tabIndex={hidden ? -1 : tabIndex}
               onKeyDown={handleKeyDown}
            />
            {clearable && !hidden && (
               <button type="button" className="searchBarUI__clear" onClick={onClear} aria-label="Șterge căutarea" title="Șterge căutarea">
                  {renderIcon("close", "searchBarUI__clearIcon")}
               </button>
            )}
         </div>

         {variant === "joined" && (
            <button type="button" className="searchBarUI__submit" onClick={submit} onPointerDown={pressPoint} disabled={disabled} aria-label={submitLabel} title={submitLabel}>
               {renderIcon("search", "searchBarUI__submitIcon")}
            </button>
         )}

         {nav && (
            <div className="searchBarUI__nav" title={nav.title || undefined}>
               <span className="searchBarUI__count" aria-live="polite">
                  {navLabel}
                  {nav.hidden > 0 && <span className="searchBarUI__hidden">{` +${nav.hidden}`}</span>}
               </span>
               <button type="button" className="searchBarUI__step searchBarUI__step--prev" onClick={nav.onPrev} disabled={!navTotal} aria-label="Rezultatul anterior">
                  ◀
               </button>
               <button type="button" className="searchBarUI__step searchBarUI__step--next" onClick={nav.onNext} disabled={!navTotal} aria-label="Rezultatul următor">
                  ▶
               </button>
            </div>
         )}

         {collapsible && (
            <ButtonUI
               className="searchBarUI__toggle"
               icon={isOpen ? "add" : "search"}
               onClick={onToggle}
               aria-label={isOpen ? closeLabel : openLabel}
               title={isOpen ? closeLabel : openLabel}
               aria-expanded={isOpen}
               aria-controls={inputId}
            />
         )}

         {actions && <div className="searchBarUI__actions">{actions}</div>}
      </div>
   );
});

const escapeRegExp = (s = "") => String(s).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

/**
 * SearchBarUI.Highlight — bucata din text care se potrivește cu căutarea, pe accent
 * (highlight din highlightText, studentsGroupsUI__highlight, studentItem__highlight,
 * examPermissionPopup__highlight).
 * Caută ca funcțiile de azi: fără să țină cont de litere mari/mici, căutarea luată
 * exact (cu diacritice), golul de la capete ignorat; „+373” și alte semne sunt text.
 *
 * text: textul întreg · query: ce s-a căutat · className: clasă în plus pe <mark>
 */
function Highlight({ text, query, className = "" }) {
   const t = String(text ?? "");
   const q = String(query ?? "").trim();
   if (!q || !t) return t;

   const lower = q.toLowerCase();
   const parts = t.split(new RegExp(`(${escapeRegExp(q)})`, "gi"));

   return (
      <>
         {parts.map((part, index) =>
            part.toLowerCase() === lower ? (
               <mark key={index} className={cx("searchBarUIHighlight", className)}>
                  {part}
               </mark>
            ) : (
               part
            ),
         )}
      </>
   );
}

SearchBarUI.Highlight = Highlight;

export default SearchBarUI;
